import type { PushSubscriptionBody } from '@cockpit/shared';

/**
 * Web Push côté navigateur (appli installée sur l'iPhone, iOS 16.4 ou plus).
 * Contraintes iOS : le push n'existe que dans l'appli ouverte depuis l'écran d'accueil (mode
 * « standalone »), jamais dans un onglet Safari ; la permission doit être demandée directement
 * dans le geste de l'utilisateur (clic), sans attente préalable.
 */

export interface PushEnvironment {
  /** iPhone, iPod ou iPad (y compris l'iPad qui se présente comme un Mac). */
  ios: boolean;
  /** Appli ouverte depuis l'écran d'accueil. */
  standalone: boolean;
  /** Service worker, PushManager et Notification disponibles. */
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  /** Clés VAPID configurées côté serveur. */
  serverEnabled: boolean;
  subscribed: boolean;
}

export type PushState = 'needs-install' | 'unsupported' | 'server-disabled' | 'denied' | 'off' | 'on';

/** État affiché par la carte « Sur iPhone » (logique pure, testée). */
export function pushState(env: PushEnvironment): PushState {
  if (env.ios && !env.standalone) return 'needs-install';
  if (!env.supported) return 'unsupported';
  if (!env.serverEnabled) return 'server-disabled';
  if (env.permission === 'denied') return 'denied';
  return env.subscribed && env.permission === 'granted' ? 'on' : 'off';
}

export const PUSH_STATE_MESSAGES: Record<PushState, string> = {
  'needs-install':
    "Sur iPhone, les notifications ne marchent que dans l'appli installée : dans Safari, Partager puis « Sur l'écran d'accueil », puis ouvre le Cockpit depuis l'écran d'accueil (iOS 16.4 ou plus).",
  unsupported: 'Ce navigateur ne gère pas les notifications push.',
  'server-disabled':
    "Les notifications push ne sont pas encore configurées sur le serveur (clés VAPID) : l'appli ouverte affiche quand même les notifications.",
  denied:
    'Les notifications sont refusées pour cette appli. Sur iPhone : Réglages > Notifications > Cockpit, puis autoriser.',
  off: 'Les notifications ne sont pas activées sur cet appareil.',
  on: 'Les notifications sont activées sur cet appareil.',
};

export function isIos(nav: Navigator = navigator): boolean {
  return /iPad|iPhone|iPod/.test(nav.userAgent) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  const media =
    typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
  return media || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Clé publique VAPID (base64 URL) → octets attendus par `pushManager.subscribe`. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

/** Abonne cet appareil (la permission doit déjà être accordée). */
export async function subscribeDevice(publicKey: string): Promise<PushSubscriptionBody> {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));
  return toBody(subscription);
}

export function toBody(subscription: PushSubscription): PushSubscriptionBody {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
}

/**
 * À la déconnexion : retire l'abonnement Web Push de CET appareil (côté serveur puis navigateur),
 * pour que le membre suivant n'y reçoive pas les notifications du précédent. Ne bloque jamais la
 * déconnexion (2 s au plus, erreurs ignorées).
 */
export async function forgetDevice(unsubscribeOnServer: (endpoint: string) => Promise<void>): Promise<void> {
  const work = (async () => {
    const subscription = await currentSubscription();
    if (!subscription) return;
    await unsubscribeOnServer(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe().catch(() => false);
  })().catch(() => undefined);
  await Promise.race([work, new Promise((resolve) => setTimeout(resolve, 2000))]);
}
